// DeveloperAuth.jsx - Login/signup UI for developer portal
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDeveloperAuth } from '../../contexts/DeveloperAuthContext';

function DeveloperAuth({ mode = 'login' }) {
  const navigate = useNavigate();
  const { signIn, signUp, resetPassword, isAuthenticated, error: authError } = useDeveloperAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated && mode !== 'forgot') {
      navigate('/developers/dashboard');
    }
  }, [isAuthenticated, navigate, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (mode === 'signup') {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (password.length < 8) {
          setError('Password must be at least 8 characters');
          setLoading(false);
          return;
        }

        const { error } = await signUp(email, password);
        if (error) {
          setError(error.message);
        } else {
          setSuccess('Check your email to confirm your account');
        }
      } else if (mode === 'login') {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error.message);
        } else {
          navigate('/developers/dashboard');
        }
      } else if (mode === 'forgot') {
        const { error } = await resetPassword(email);
        if (error) {
          setError(error.message);
        } else {
          setSuccess('Check your email for password reset instructions');
        }
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const titles = {
    login: 'Sign in to Developer Portal',
    signup: 'Create Developer Account',
    forgot: 'Reset Password'
  };

  const subtitles = {
    login: 'Access your API keys and usage statistics',
    signup: 'Request API access for MySkinAI',
    forgot: 'Enter your email to receive reset instructions'
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">M</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">MySkinAI</h1>
              <p className="text-xs text-gray-500">Developer Portal</p>
            </div>
          </Link>
          <Link
            to="/api-docs"
            className="text-gray-600 hover:text-gray-900 text-sm font-medium"
          >
            API Docs
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">{titles[mode]}</h2>
            <p className="text-gray-600 mt-2">{subtitles[mode]}</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
            <div className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                  placeholder="you@company.com"
                  required
                />
              </div>

              {/* Password */}
              {mode !== 'forgot' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                    placeholder="Enter your password"
                    required
                    minLength={mode === 'signup' ? 8 : undefined}
                  />
                </div>
              )}

              {/* Confirm Password (signup only) */}
              {mode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                    placeholder="Confirm your password"
                    required
                    minLength={8}
                  />
                </div>
              )}

              {/* Error Message */}
              {(error || authError) && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-sm text-red-600">{error || authError}</p>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="p-4 bg-green-50 border border-green-100 rounded-xl">
                  <p className="text-sm text-green-600">{success}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <span className="inline-flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Processing...
                  </span>
                ) : (
                  mode === 'login' ? 'Sign In' :
                  mode === 'signup' ? 'Create Account' :
                  'Send Reset Link'
                )}
              </button>

              {/* Forgot Password Link */}
              {mode === 'login' && (
                <div className="text-center">
                  <Link
                    to="/developers/forgot-password"
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    Forgot your password?
                  </Link>
                </div>
              )}
            </div>
          </form>

          {/* Mode Toggle */}
          <div className="text-center mt-6">
            {mode === 'login' ? (
              <p className="text-gray-600 text-sm">
                Don't have an account?{' '}
                <Link to="/developers/signup" className="text-gray-900 font-medium hover:underline">
                  Sign up
                </Link>
              </p>
            ) : mode === 'signup' ? (
              <p className="text-gray-600 text-sm">
                Already have an account?{' '}
                <Link to="/developers/login" className="text-gray-900 font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            ) : (
              <p className="text-gray-600 text-sm">
                Remember your password?{' '}
                <Link to="/developers/login" className="text-gray-900 font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>MySkinAI Developer Portal</p>
        </div>
      </footer>
    </div>
  );
}

export default DeveloperAuth;
